<template>
  <div
    class="vehicle-finder"
    :class="[
      `vehicle-finder--${media.hasImage ? 'has-image' : 'no-image'}`,
      {
        [`section-spacing__bottom section-spacing__bottom--${liquid.paddingBottom}`]:
          liquid.paddingBottom,
        [`section-spacing__top section-spacing__top--${liquid.paddingTop}`]:
          liquid.paddingTop,
        'critical-component-clear': template === 'section',
      }
    ]"
    :style="getBackgroundColour('parent')"
  >
    <div class="vehicle-finder__container">
      <div
        class="vehicle-finder__content"
        :style="getBackgroundColour('content')"
      >
        <div
          v-if="media.hasImage"
          class="vehicle-finder__media"
        >
          <div
            v-if="media.enableOverlay"
            class="vehicle-finder__overlay"
          />

          <art-direction
            desktop-breakpoint="m"
            :image="media.image"
            :image-desktop="media.imageDesktop"
            :lazyload="media.lazyload"
            :loaded-animation="false"
            :padding="false"
            :ratio="false"
            :ratio-desktop="false"
          />
        </div>

        <div class="vehicle-finder__text-container">
          <div class="grid">
            <div
              class="vehicle-finder__text col xs-span m4 l6"
              :class="media.hasImage ? 'l6' : 'l7'"
            >
              <heading-copy
                :classes="{
                  copy: headingClasses.copy,
                  headingElement: heading.headingElement,
                  subheading: headingClasses.subheading,
                }"
                :content="headingContent"
              />
            </div>

            <div
              v-if="showActiveContentIgnoringSearchAgain"
              class="
                vehicle-finder__active-vehicle-buttons
                vehicle-finder__active-vehicle-buttons--desktop
                col l5
              "
            >
              <btn
                v-if="showActiveContent"
                :label="$string('vehicle_finder.cta_labels.search_again')"
                :modes="heading.buttonColour"
                modifiers="primary"
                @click="handleSearchAgain"
              />

              <btn
                :label="differentCarString"
                :modes="heading.buttonColour"
                modifiers="secondary"
                @click="handleOpenGarage"
              />
            </div>

            <div
              v-if="icons.length"
              class="vehicle-finder__icons col xs-span"
            >
              <div
                v-for="(icon, index) of icons"
                :key="`icon-${index}`"
                class="vehicle-finder__icon"
              >
                <component
                  :is="`icon-${icon.icon}`"
                  aria-hidden="true"
                />

                <p
                  class="vehicle-finder__icon-title text-body-s-regular"
                  v-text="icon.title"
                />
              </div>
            </div>
          </div>
        </div>

        <div
          v-if="showActiveContent"
          class="vehicle-finder__form"
        >
          <garage-hero-banner
            template="finder"
            :vehicle="activeVehicle"
          />
        </div>

        <vehicle-finder-form
          v-else
          ref="form"
          class="vehicle-finder__form"
          :namespace="heading.headingId"
          :overlay="overlay"
          :template="template"
        />

        <div
          v-if="showActiveContentIgnoringSearchAgain"
          class="
            vehicle-finder__active-vehicle-buttons
            vehicle-finder__active-vehicle-buttons--mobile
          "
        >
          <btn
            v-if="showActiveContent"
            :label="$string('vehicle_finder.cta_labels.search_again')"
            :modes="heading.buttonColour"
            modifiers="secondary block center"
            @click="handleSearchAgain"
          />

          <btn
            class-label="text-body-xs-underline"
            :label="differentCarString"
            :modes="heading.buttonColour"
            modifiers="text block center"
            @click="handleOpenGarage"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>

/**
 * Vue: Vehicle finder (vehicle-finder)
 * -----------------------------------------------------------------------------
 * Component shared between modal and section templates.
 *
 * @param {Object} [activeVehicleContent] - Active vehicle settings.
 * @param {Boolean} [activeVehicleContent.enable] - Enable active vehicle
 * content settings.
 * @param {Object} [activeVehicleContent.heading] - Heading copy content
 * settings for when an active vehicle is set.
 * @param {Object} heading - Heading copy content settings.
 * @param {Array<Object>} [icons] - Icons to display.
 * @param {Object} liquid - Shopify section object.
 * @param {String} liquid.paddingBottom - Section bottom padding.
 * @param {String} liquid.paddingTop - Section top padding.
 * @param {String} liquid.sectionId - Section ID.
 * @param {Object} media - Media settings.
 * @param {String} media.backgroundColour - Section background colour.
 * @param {Boolean} [media.enableOverlay] - Show overlay over image.
 * @param {Boolean} media.hasImage - Section uses image.
 * @param {Object} [media.image] - Mobile image.
 * @param {Object} [media.desktopImage] - Desktop image.
 * @param {Boolean} [media.lazyload] - Lazyload background image.
 * @param {Object} overlay - Overlay API.
 * @param {String} template - Component template, either `modal` or `section`.
 *
 */
import { mapActions, mapGetters } from 'vuex'

import ArtDirection from '~global/images/art-direction'
import Btn from '~global/btn/btn'
import GarageHeroBanner from '~async/garage-hero-banner/garage-hero-banner'
import HeadingCopy from '~async/heading-copy/heading-copy'
import VehicleFinderForm from './vehicle-finder-form/vehicle-finder-form'

import IconLock from '~icons/general/lock.svg'
import IconManual from '~icons/general/manual.svg'
import IconReturns from '~icons/general/returns.svg'
import IconSpanner from '~icons/general/spanner.svg'
import IconVan from '~icons/general/van.svg'

import { focusable } from '~/helpers/accessibility'

export default {
  name: 'VehicleFinder',

  components: {
    ArtDirection,
    Btn,
    GarageHeroBanner,
    HeadingCopy,
    IconLock,
    IconManual,
    IconReturns,
    IconSpanner,
    IconVan,
    VehicleFinderForm,
  },

  props: {
    activeVehicleContent: {
      type: Object,
      default: () => ({
        enable: false,
        heading: {},
      }),
    },
    heading: {
      type: Object,
      default: () => ({}),
      required: true,
    },
    icons: {
      type: Array,
      default: () => [],
    },
    liquid: {
      type: Object,
      default: () => ({
        paddingBottom: '',
        paddingTop: '',
        sectionId: '',
      }),
      required: true,
    },
    media: {
      type: Object,
      default: () => ({
        backgroundColour: '--color-neutral-5',
        enableOverlay: false,
        hasImage: false,
        image: {},
        imageDesktop: {},
        lazyload: true,
      }),
      required: true,
    },
    overlay: {
      type: Object,
      default: () => ({}),
    },
    template: {
      type: String,
      default: 'section',
      required: true,
      validator: (value) => ['modal', 'section'].includes(value),
    },
  },

  data() {
    return {
      searchAgain: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapGetters({
      activeVehicle: 'garage/getActiveVehicle',
    }),

    /**
     * Compute use different car button string.
     * @returns {String}
     */
    differentCarString() {
      return this.searchAgain
        ? this.$string('garage.cta_labels.use_car')
        : this.$string('garage.cta_labels.different_car')
    },

    /**
     * Compute heading copy classes.
     * @returns {Object}
     */
    headingClasses() {
      if (this.media.hasImage) {
        return {
          copy: 'text-body-s-regular text-body-m-regular-desktop',
          subheading: 'text-heading-m text-heading-xl-desktop',
        }
      }

      return {
        copy: 'text-body-s-regular',
        subheading: 'text-heading-m',
      }
    },

    /**
     * Compute heading content.
     * @returns {Object}
     */
    headingContent() {
      if (this.showActiveContent) {
        return {
          ...this.heading,
          ...this.activeVehicleContent.heading,
        }
      }

      return this.heading
    },

    /**
     * Compute if active vehicle content should be shown.
     * @returns {Boolean}
     */
    showActiveContent() {
      return (
        this.activeVehicle &&
        this.activeVehicleContent.enable &&
        !this.searchAgain
      )
    },

    /**
     * Compute if active vehicle content should be shown, ignores search again.
     * - Used to show buttons for switching between form and active vehicle.
     * @returns {Boolean}
     */
    showActiveContentIgnoringSearchAgain() {
      return (
        this.activeVehicle &&
        this.activeVehicleContent.enable
      )
    },
  },

  watch: {

    /**
     * Watch active vehicle to reset search again behaviour.
     */
    activeVehicle() {
      this.searchAgain = false
    },
  },

  methods: {

    /**
     * Map Vuex actions.
     */
    ...mapActions({
      openFinderFlow: 'garage/openFinderFlow',
    }),

    /**
     * Get background colour styles based on element.
     * @param {String} location - Location of element.
     * @returns {Object}
     */
    getBackgroundColour(location) {
      if (this.media.backgroundColour) {
        const styles = {
          backgroundColor: `var(${this.media.backgroundColour})`,
        }

        if (location === 'parent' && this.template === 'modal') {
          return styles
        }

        if (location === 'content' && this.template === 'section') {
          return styles
        }
      }

      return {}
    },

    /**
     * Handle search again.
     * - Set focus to first focusable element in vehicle finder form.
     */
    async handleSearchAgain() {
      this.searchAgain = true

      await this.$nextTick()
      const focusableElements = focusable(this.$refs.form.$el)
      cnvs.Focus(focusableElements[0])
    },

    /**
     * Handle open garage.
     */
    handleOpenGarage() {
      this.searchAgain = false
      this.openFinderFlow('garage')
    },

    /**
     * Shopify section editor select event.
     */
    shopifySectionSelect() {
      if (this.template !== 'modal') {
        return
      }

      this.openFinderFlow('finder')
    },

    /**
     * Shopify section editor deselect event.
     */
    shopifySectionDeselect() {
      if (this.template !== 'modal') {
        return
      }

      this.overlay.close()
    },
  },
}
</script>

<style lang="scss">
@import './vehicle-finder';
</style>
